import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { backLinkClasses, Icon, Page, PageHeader } from '@evogard/ui';
import type { Period } from '@evogard/contract';
import { BackButton } from '../../components/BackButton.js';
import { useSessionContext } from '../../lib/session.js';
import { AwardHomeReport, awardDailyQuery } from './AwardHomeReport.js';
import { periodLabel } from './common.js';

/**
 * O RELATÓRIO de uma premiação — `/sales/management/portfolio?award=…&month=…`.
 *
 * Aberto pelo cartão da premiação no Início do Comercial: o gráfico, os KPIs
 * "Premiação ganha" e "Deixa de ganhar" e a tabela "Dados" (`AwardHomeReport`).
 * Até 28/10/2026 havia uma segunda versão, com resumo e a lista longa, para
 * quem chegava pelo Painel de Gestão; o painel saiu, e ela junto.
 *
 * Para que ela existe: o demonstrativo diz "R$ 1.890,00", e esta é a tela onde
 * se confere de onde veio — boleto a boleto, com a parte de cada um.
 *
 * O endereço guarda o nome antigo (`/sales/management/portfolio`) para os links
 * salvos continuarem abrindo. `origin` e `state` continuam aceitos na busca
 * pelo mesmo motivo, e não mudam mais nada.
 */
export function AwardPortfolioScreen() {
  const { award, month, consultant } = useSearch({
    from: '/authenticated/sales/management/portfolio',
  });
  const { data: context } = useSessionContext();
  const daily = useQuery(
    awardDailyQuery(context?.currentTenant.id, consultant, award, month),
  );
  const title = daily.data?.estado === 'medido' ? daily.data.title : undefined;

  return (
    <Page measure="cheia">
      {/*
       * Volta para onde se veio (`BackButton`). Sem página anterior, o Início de
       * quem estava sendo olhado, no mês da premiação.
       */}
      <BackButton
        fallback={
          <Link
            to="/sales/home"
            search={{ consultant, month }}
            className={backLinkClasses()}
          >
            <Icon nome="seta-esquerda" size={16} />
            Início
          </Link>
        }
      />

      <PageHeader title="Relatório da premiação">
        {title === undefined
          ? `Competência de ${periodLabel(month as Period)}.`
          : `${title} · ${periodLabel(month as Period)}${
              /*
               * De quem é a lista, quando não é de quem está olhando — pelo
               * código, que é o que o endereço traz.
               */
              consultant === undefined ? '' : ` · consultor ${consultant}`
            }.`}
      </PageHeader>

      <AwardHomeReport award={award} month={month} consultant={consultant} />
    </Page>
  );
}
